import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";

import useSubmitData from "@/hooks/useSubmitData";
import i18n from "@/translate/i18n";
import config from "@/config";

export function usePaymentDataForm({ data, setData, setError, setIsFetching }) {
  const history = useHistory();
  const { id, token } = useParams();
  const { submitData } = useSubmitData(setError, setIsFetching);

  const [customerDataValidated, setCustomerDataValidated] = useState(false);
  const [isEditingCbu, setIsEditingCbu] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState(null);
  const [isValidEmail, setIsValidEmail] = useState(
    Boolean(data?.customer?.email),
  );
  const [emailHasErrors, setEmailHasErrors] = useState(false);

  const initialAcceptedTerms = Boolean(data?.customer?.terms_accepted_at);
  const initialCountryIso =
    data?.customer?.country_iso || data?.customer_country_iso || "";
  const initialDocumentType =
    data?.customer?.document_type || data?.customer_document_types?.[0] || "";
  const initialDocumentNumber = data?.customer?.document_number || "";
  const initialCbu = data?.bank_payment_data?.account_id || "";

  const [documentInstructions, setDocumentInstructions] = useState({
    show: false,
    message:
      initialDocumentNumber === ""
        ? i18n.t("form.documentNumberPresentInfo")
        : i18n.t("form.documentNumberInfo"),
    type: "info",
  });

  const [accountInstructions, setAccountInstructions] = useState({
    show: false,
    message: i18n.t("form.accountNumberInfo"),
    type: "info",
  });

  const validateField = (fieldValue, fieldType) => {
    if (!fieldValue || fieldValue.trim() === "") {
      return {
        isValid: false,
        isEmpty: true,
        message: i18n.t(
          `form.invalid${fieldType === "document" ? "Document" : "Account"}`,
        ),
      };
    }
    if (fieldType === "document") {
      const cleanedValue = fieldValue.replace(/\D/g, "");
      const successMessageKey = initialDocumentNumber
        ? "form.documentNumberInfo"
        : "form.documentNumberPresentInfo";
      return {
        isValid: cleanedValue.length === 11,
        isEmpty: false,
        message:
          cleanedValue.length === 11
            ? i18n.t(successMessageKey)
            : i18n.t("form.invalidDocument"),
      };
    }
    const hasAtLeastOneNonSpaceChar = /\S/.test(fieldValue);
    return {
      isValid: hasAtLeastOneNonSpaceChar,
      isEmpty: false,
      message: hasAtLeastOneNonSpaceChar
        ? i18n.t("form.accountNumberInfo")
        : i18n.t("form.invalidAccount"),
    };
  };

  useEffect(() => {
    if (currentFormValues && data?.type === "withdrawal") {
      const isDocumentValid =
        initialDocumentNumber ||
        validateField(currentFormValues.document_number, "document").isValid;
      const isDocumentType = initialDocumentType
        ? Boolean(currentFormValues.document_type || initialDocumentType)
        : true;
      const isAccountValid = validateField(
        currentFormValues.account_id,
        "account",
      ).isValid;
      const areTermsAccepted =
        !config.requireTerms || Boolean(currentFormValues.accepted_terms);
      setCustomerDataValidated(
        isDocumentValid && isDocumentType && areTermsAccepted && isAccountValid,
      );
    } else {
      setCustomerDataValidated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFormValues,
    data?.type,
    initialDocumentType,
    initialDocumentNumber,
  ]);

  const handleFieldFocus = (
    fieldName,
    setFieldError,
    setInstructions,
    options = {},
  ) => {
    const fieldType = fieldName === "document_number" ? "document" : "account";
    const { showInfoOnFocus = true } = options;
    if (showInfoOnFocus) {
      let messageKey = `form.${fieldType}NumberInfo`;
      if (fieldType === "document" && !initialDocumentNumber)
        messageKey = "form.documentNumberPresentInfo";
      setInstructions({
        show: true,
        message: i18n.t(messageKey),
        type: "info",
      });
    }
    setFieldError(fieldName, "");
  };

  const validEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailFocus = (setFieldError) => {
    setEmailHasErrors(false);
    setFieldError("email", "");
  };

  const handleEmailBlur = (emailAddress, setFieldTouched, setFieldError) => {
    setFieldTouched("email");
    setFieldError("email", "");
    if (validEmail(emailAddress)) {
      setEmailHasErrors(false);
    } else {
      setFieldError("email", "error");
      setEmailHasErrors(true);
    }
  };

  const handleFieldBlur = (
    fieldName,
    fieldValue,
    setFieldTouched,
    setFieldError,
    setInstructions,
    options = {},
  ) => {
    const fieldType = fieldName === "document_number" ? "document" : "account";
    const { showInfoOnValid = true, onValidCallback } = options;
    setFieldTouched(fieldName);
    setFieldError(fieldName, "");
    const validation = validateField(fieldValue, fieldType);
    if (validation.isEmpty) {
      setInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError(fieldName, validation.message);
    } else if (validation.isValid) {
      if (showInfoOnValid) {
        setInstructions({
          show: true,
          message: validation.message,
          type: "info",
        });
      } else {
        setInstructions({ show: false, message: "", type: "info" });
      }
      if (onValidCallback) onValidCallback();
    } else {
      setInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError(fieldName, validation.message);
    }
  };

  const handleFieldChange = (
    fieldName,
    target,
    setFieldTouched,
    setFieldValue,
    setInstructions,
    options = {},
  ) => {
    const { customLogic, clearInstructionsOnChange = false } = options;
    setFieldTouched(fieldName, true);
    if (clearInstructionsOnChange)
      setInstructions({ show: false, message: "", type: "info" });
    if (customLogic) {
      customLogic(target, setFieldValue);
    } else {
      setFieldValue(fieldName, target.value);
    }
  };

  const handleSubmit = ({
    accepted_terms,
    country_iso,
    document_type,
    document_number,
    email,
    merchant_request_amount,
    bank_payment_data,
    account_id,
  }) => {
    const cleanedDocumentNum = document_number.replace(/\D/g, "");
    const transactionData = {
      customer: {
        country_iso,
        document_type,
        document_number: cleanedDocumentNum,
        email,
      },
      merchant_request_amount,
      bank_payment_data: { ...bank_payment_data, account_id },
    };

    setData((state) => ({
      ...state,
      bank_payment_data: {
        ...state.payment_data,
        paymentDataValidated: true,
        account_id,
      },
      accepted_terms,
      customer_data: { ...state.customer, ...transactionData.customer },
    }));

    if (customerDataValidated) {
      submitData({
        ...data,
        merchant_request_amount,
        bank_payment_data: {
          ...data?.bank_payment_data,
          paymentDataValidated: true,
          account_id,
        },
        accepted_terms,
        customer: { ...data.customer, ...transactionData.customer },
      });
    } else {
      history.push(`/${id}/${token}${history.location.search}`);
    }
  };

  return {
    // state
    customerDataValidated,
    isEditingCbu,
    setIsEditingCbu,
    isValidEmail,
    setIsValidEmail,
    emailHasErrors,
    documentInstructions,
    setDocumentInstructions,
    accountInstructions,
    setAccountInstructions,
    currentFormValues,
    setCurrentFormValues,
    // derived
    initialAcceptedTerms,
    initialCountryIso,
    initialDocumentType,
    initialDocumentNumber,
    initialCbu,
    // handlers
    validateField,
    validEmail,
    handleEmailFocus,
    handleEmailBlur,
    handleFieldFocus,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit,
    // navigation
    history,
    id,
    token,
  };
}
